<template>
  <div
    :id="`column-${sectionIndex}-${columnIndex}`"
    :key="divKey"
    data-testid="mj-column"
    class="relative"
    :style="{ width: columnWidth, verticalAlign: valign }"
    style="
      display: inline-block;
      direction: ltr;
      font-size: 13px;
      text-align: left;
      box-sizing: border-box;
    "
    @mouseover="resizeHovered = true"
    @mouseleave="resizeHovered = false"
  >
    <table role="presentation" cellpadding="0" cellspacing="0" width="100%">
      <tbody
        v-if="columnHasNoChildren"
        data-testid="empty-drop-zone"
        class="empty"
        @click="openAddBlockDialog"
      >
        <VueDraggable
          :model-value="column.children"
          :animation="150"
          :group="{ name: 'blocks', pull: 'clone' }"
          handle=".movement-drag-handle"
          @mouseover="hovered = true"
          @mouseleave="hovered = false"
          @update="onUpdate"
          @add="onAdd"
          @start="onStart"
          @end="onEnd"
        >
          <Transition mode="out-in">
            <svg
              v-if="!hovered"
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="16"
                cy="16"
                r="15"
                stroke="#6DA8FF"
                stroke-width="2"
                stroke-miterlimit="2.61313"
                stroke-linejoin="round"
              />
              <path
                d="M18.125 8.5C18.125 7.94772 17.6773 7.5 17.125 7.5H14.875C14.3227 7.5 13.875 7.94772 13.875 8.5V13.875H8.5C7.94772 13.875 7.5 14.3227 7.5 14.875V17.125C7.5 17.6773 7.94772 18.125 8.5 18.125H13.875V23.5C13.875 24.0523 14.3227 24.5 14.875 24.5H17.125C17.6773 24.5 18.125 24.0523 18.125 23.5V18.125H23.5C24.0523 18.125 24.5 17.6773 24.5 17.125V14.875C24.5 14.3227 24.0523 13.875 23.5 13.875H18.125V8.5Z"
                fill="#6DA8FF"
              />
            </svg>

            <svg
              v-else
              width="36"
              height="36"
              viewBox="0 0 36 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="18" cy="18" r="16" fill="#6DA8FF" />
              <circle
                cx="18"
                cy="18"
                r="17"
                stroke="white"
                stroke-opacity="0.4"
                stroke-width="2"
                stroke-miterlimit="2.61313"
                stroke-linejoin="round"
              />
              <path
                d="M20.125 10.5C20.125 9.94772 19.6773 9.5 19.125 9.5H16.875C16.3227 9.5 15.875 9.94772 15.875 10.5V15.875H10.5C9.94772 15.875 9.5 16.3227 9.5 16.875V19.125C9.5 19.6773 9.94772 20.125 10.5 20.125H15.875V25.5C15.875 26.0523 16.3227 26.5 16.875 26.5H19.125C19.6773 26.5 20.125 26.0523 20.125 25.5V20.125H25.5C26.0523 20.125 26.5 19.6773 26.5 19.125V16.875C26.5 16.3227 26.0523 15.875 25.5 15.875H20.125V10.5Z"
                fill="white"
              />
            </svg>
          </Transition>
        </VueDraggable>
      </tbody>
      <tbody v-else-if="column">
        <tr>
          <td :style="innerAttributes ? tableStyle : columnStyle">
            <table
              role="presentation"
              cellpadding="0"
              cellspacing="0"
              width="100%"
              class=""
              :style="innerAttributes ? columnStyle : {}"
            >
              <tbody>
                <VueDraggable
                  :model-value="column.children"
                  :animation="150"
                  :group="{ name: 'blocks', pull: 'clone' }"
                  handle=".movement-drag-handle"
                  @update="onUpdate"
                  @add="onAdd"
                  @start="onStart"
                  @end="onEnd"
                >
                  <mjmlBlock
                    v-for="(block, blockIndex) in column.children"
                    :key="block.uid"
                    :block="block"
                    :column-index="columnIndex"
                    :section-index="sectionIndex"
                    :block-index="blockIndex"
                  ></mjmlBlock>
                </VueDraggable>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts" setup>
  import mjmlBlock from "./mjmlBlock.vue";

  import interact from "interactjs";
  import { useResizeColumnsStore } from "@/stores/resizeColumnsStore";
  import { useNotificationsStore } from "@/stores/notifications";
  import { useDialogsStore } from "@/stores/dialogs";
  import { useDraggingStore } from "@/stores/draggingStore";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useI18n } from "vue-i18n";
  import { VueDraggable } from "vue-draggable-plus";
  import { getSectionContentWidth } from "@/utils/columnResizeBounds";
  import {
    StyleValue,
    computed,
    ref,
    onBeforeUnmount,
    watch,
    onMounted,
  } from "vue";

  const props = defineProps<{
    columnIndex: number;
    sectionIndex: number;
    sectionLocked: boolean;
  }>();

  const hovered = ref(false);
  const resizeHovered = ref(false);

  const resizeColumnsStore = useResizeColumnsStore();
  const notificationsStore = useNotificationsStore();
  const dialogsStore = useDialogsStore();
  const draggingStore = useDraggingStore();
  const mjmlStore = useMjmlStore();
  const { t } = useI18n();

  const column = computed(() => {
    return mjmlStore.getMjml().children[0].children[props.sectionIndex]
      ?.children[props.columnIndex];
  });

  const columnCount = computed(() => {
    if (mjmlStore.getMjml().children[0].children[props.sectionIndex]) {
      return mjmlStore.getMjml().children[0].children[props.sectionIndex]
        ?.children.length;
    }
    return 0;
  });

  const isLastColumn = computed(() => {
    return props.columnIndex === columnCount.value - 1;
  });

  const bgWidth = computed(() => {
    return mjmlStore.getBodyWidth;
  });

  const innerAttributes = computed(() => {
    return column.value?.attributes["inner-attributes"];
  });

  const tableStyle = computed(() => {
    if (!column.value) return {};
    if (
      column.value.attributes.padding &&
      column.value.attributes["inner-attributes"]
    ) {
      return {
        padding: column.value.attributes.padding,
      };
    }
    return {};
  });

  const columnWidth = computed(() => {
    if (mjmlStore.isMobileFirstEnabled) {
      const doNotStackOnMobile =
        mjmlStore.getMjml().children[0].children[props.sectionIndex]
          ?.attributes["in-group"];

      if (mjmlStore.isMobileView && !doNotStackOnMobile) {
        return "100%";
      }
    }

    return column.value?.attributes.width || "100%";
  });

  const columnHasNoChildren = computed(() => {
    if (!column.value) return false;
    if (column.value.children == null) {
      return true;
    }

    return column.value.children.length === 0;
  });

  const valign = computed(() => {
    if (
      column.value &&
      column.value.attributes &&
      column.value.attributes["vertical-align"]
    ) {
      return column.value.attributes["vertical-align"];
    } else {
      return "top";
    }
  });

  const divKey = computed(() => {
    return JSON.stringify({
      verticalAlign: valign.value,
    });
  });

  const columnStyle = computed<StyleValue>(() => {
    type Style = {
      borderRadius: string;
      verticalAlign: string;
      [key: string]: string;
    };

    const style: Style = {
      borderRadius: "0px",
      verticalAlign: valign.value,
    };

    if (!column.value) {
      return style;
    }

    if (
      column.value.attributes.padding &&
      !column.value.attributes["inner-attributes"]
    ) {
      style.padding = column.value.attributes.padding;
    }

    if (column.value.attributes["background-color"]) {
      style.backgroundColor = column.value.attributes["background-color"];
    }

    if (column.value.attributes["column-gradient"]) {
      style.backgroundImage = column.value.attributes["column-gradient"];
    }

    if (column.value.attributes.border) {
      style.border = column.value.attributes.border;
    }

    if (column.value.attributes["border-top"]) {
      style.borderTop = column.value.attributes["border-top"];
    }

    if (column.value.attributes["border-bottom"]) {
      style.borderBottom = column.value.attributes["border-bottom"];
    }

    if (column.value.attributes["border-left"]) {
      style.borderLeft = column.value.attributes["border-left"];
    }

    if (column.value.attributes["border-right"]) {
      style.borderRight = column.value.attributes["border-right"];
    }

    if (column.value.attributes["border-radius"]) {
      style.borderRadius = column.value.attributes["border-radius"];
    }

    return style;
  });

  const isPaddingZero = computed(() => {
    const pad = column.value?.attributes.padding;
    let isZero = true;
    if (pad) {
      const paddingParts = pad.split(" ");
      paddingParts.forEach((part: string) => {
        if (parseInt(part) !== 0) {
          isZero = false;
        }
      });
    }
    return isZero;
  });

  function onStart(evt: any) {
    // Save starting section and column for later reordering
    draggingStore.startDragging({
      sectionIndex: props.sectionIndex,
      columnIndex: props.columnIndex,
      blockIndex: evt.oldIndex,
    });
  }

  function onEnd() {
    draggingStore.stopDragging();
  }

  function onAdd(evt: any) {
    const droppedItem = evt.item;
    if (evt.from.parentNode.id == "blocks") {
      const blockType = droppedItem.dataset.block;

      let customBlockData;

      if (blockType === "apiBlock" && droppedItem.dataset.apiBlockName) {
        customBlockData = droppedItem.dataset.apiBlockName;
      } else if (droppedItem.dataset.customBlockData) {
        customBlockData = JSON.parse(droppedItem.dataset.customBlockData);
      }

      if (blockType === "carousel") {
        if (
          mjmlStore.getSelectedSectionByIndex(props.sectionIndex).children
            .length > 1
        ) {
          notificationsStore.createNotification({
            type: "error",
            text: t("mjml.carousel_one_column_error_message"),
          });

          droppedItem.parentNode.removeChild(droppedItem);
          draggingStore.draggingSlider = false;
          return;
        }
      }

      mjmlStore.addBlock({
        sectionIndex: props.sectionIndex,
        columnIndex: props.columnIndex,
        blockIndex: evt.newIndex,
        type: blockType,
        customBlockData: customBlockData,
      });
    } else {
      const dragStartOptions = draggingStore.dragStart;
      const index =
        evt.newIndex >= column.value?.children.length
          ? column.value.children.length - 1
          : evt.newIndex;
      // column.value?.children.splice(index, 1);

      if (dragStartOptions) {
        mjmlStore.reorderBlocksBetweenColumns({
          oldSectionIndex: dragStartOptions.sectionIndex,
          oldColumnIndex: dragStartOptions.columnIndex,
          oldIndex: evt.oldIndex,
          newIndex: index,
          sectionIndex: props.sectionIndex,
          columnIndex: props.columnIndex,
        });
      }
    }
    draggingStore.draggingSlider = false;
  }

  function onUpdate(evt: any) {
    mjmlStore.reorderBlocks({
      sectionIndex: props.sectionIndex,
      columnIndex: props.columnIndex,
      oldIndex: evt.oldIndex,
      newIndex: evt.newIndex,
    });
    // If selected set new position as selected
  }

  function openAddBlockDialog() {
    dialogsStore.openBlockDialog({
      direction: null,
      options: {
        sectionIndex: props.sectionIndex,
        columnIndex: props.columnIndex,
        blockIndex: 0, //New BLock is always first in current column
      },
    });
  }

  function enableResize() {
    const element = document.getElementById(
      `column-${props.sectionIndex}-${props.columnIndex}`
    );
    if (
      !props.sectionLocked &&
      !isLastColumn.value &&
      element &&
      columnCount.value > 1 &&
      !mjmlStore.isMobileView
    ) {
      const interactable = interact(
        `#column-${props.sectionIndex}-${props.columnIndex}`
      ).resizable({
        edges: {
          left: false,
          right:
            props.columnIndex === columnCount.value - 1
              ? false
              : `#handle-${props.sectionIndex}-${props.columnIndex}`,
          top: false,
          bottom: false,
        },
      });

      interactable.on("resizestart", () => {
        const handler = document.getElementById(
          `handle-${props.sectionIndex}-${props.columnIndex}`
        );
        resizeColumnsStore.setNewValue(true);
        resizeColumnsStore.resizingColumn = {
          sectionIndex: props.sectionIndex,
          columnIndex: props.columnIndex,
          blockIndex:
            handler && handler.dataset && handler.dataset.blockindex
              ? Number(handler.dataset.blockindex)
              : -1,
        };
      });

      interactable.on("resizemove", (event) => {
        // Column percentages are relative to the section's content box, not to
        // the body width. The store clamps the request to what fits.
        const contentWidth =
          getSectionContentWidth(
            mjmlStore.getMjml().children[0].children[props.sectionIndex],
            bgWidth.value
          ) || bgWidth.value;

        mjmlStore.resizeColumns({
          sectionIndex: props.sectionIndex,
          columnIndex: props.columnIndex,
          value: Math.round((100 / contentWidth) * event.rect.width),
        });
      });

      interactable.on("resizeend", () => {
        resizeColumnsStore.setNewValue(false);
        resizeColumnsStore.resizingColumn = {
          sectionIndex: -1,
          columnIndex: -1,
          blockIndex: -1,
        };
      });
    }
  }

  watch(columnCount, (newVal) => {
    if (newVal > 1) {
      enableResize();
    } else {
      interact(`#column-${props.sectionIndex}-${props.columnIndex}`).unset();
    }
  });

  watch(isLastColumn, (newVal) => {
    if (newVal) {
      interact(`#column-${props.sectionIndex}-${props.columnIndex}`).unset();
    } else {
      enableResize();
    }
  });

  function removeInteract() {
    const element = document.getElementById(
      `column-${props.sectionIndex}-${props.columnIndex}`
    );
    if (element) {
      interact(`#column-${props.sectionIndex}-${props.columnIndex}`).unset();
    }
  }

  function resetInteract() {
    removeInteract();
    enableResize();
  }

  onMounted(() => {
    enableResize();
    window.$event.on("reset-interact", resetInteract);
  });

  onBeforeUnmount(() => {
    removeInteract();
    window.$event.off("reset-interact", resetInteract);
  });
</script>

<style>
  /* we will explain what these classes do next! */
  .v-enter-active,
  .v-leave-active {
    transition: all 65ms ease-out;
    transform: scale(0.95);
  }

  .v-enter-from,
  .v-leave-to {
    opacity: 0;
    transform: scale(1);
  }
</style>
